import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=user
  const [ready, setReady] = useState(false);

  // Bootstrap: check current session exactly once on mount.
  // Intentionally empty deps: nothing here changes across the app lifecycle.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        // 401 on cold boot is normal (not logged in); do not log.
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- bootstrap runs once on mount

  // Re-pull /auth/me — used after Stripe checkout returns so the local
  // user object reflects the new subscription_status without a page reload.
  //
  // Hoisted OUT of the useMemo below and wrapped in useCallback with an
  // empty dep array so its function identity is stable for the lifetime of
  // the provider. Historically this lived inside the useMemo which depended
  // on `[user, ready]`, so every setUser() minted a new `refresh` — any
  // consumer with `refresh` in a useEffect dep array (SubscribeResult's
  // polling loop is the tripwire) would remount its effect on every
  // refresh call, cancelling its own polling closure mid-flight and never
  // reaching setState("paid").
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const value = useMemo(() => {
    const login = async (email, password) => {
      try {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return { ok: true, user: data };
      } catch (e) {
        return { ok: false, error: formatApiError(e) };
      }
    };

    const register = async (payload) => {
      try {
        const { data } = await api.post("/auth/register", payload);
        setUser(data);
        return { ok: true, user: data };
      } catch (e) {
        return { ok: false, error: formatApiError(e) };
      }
    };

    const logout = async () => {
      try {
        await api.post("/auth/logout");
      } catch (e) {
        // Network hiccup is non-fatal — clear local state anyway.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[auth] logout request failed", e?.message || e); // eslint-disable-line no-console
        }
      }
      setUser(false);
    };

    const updateProfile = async (payload) => {
      try {
        const { data } = await api.patch("/auth/profile", payload);
        setUser(data);
        return { ok: true, user: data };
      } catch (e) {
        return { ok: false, error: formatApiError(e) };
      }
    };

    return { user, ready, login, register, logout, updateProfile, refresh };
  }, [user, ready, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
